import { Loading } from "@components/Loading";
import { Modal } from "@components/Modal";
import { useFirebaseCachedImageUrl } from "@helpers/useFirebaseCachedImageUrl";
import { useSimpleCollection } from "@helpers/useSimpleCollection";
import { useSimpleDocument } from "@helpers/useSimpleDocument";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { useLayoutEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useBranding } from "src/helpers/useBranding";

export const PublicBettingResults = () => {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const teamParam = searchParams.get("t"); // team_id or team_name
  const { t } = useTranslation("challenges");
  const { getTranslatedValue } = useTranslatedKey();

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentTransaction | null>(null);

  const [teams, tLoading] = useSimpleCollection<Team>(`events/${eventId}/teams`);
  const [challenges, cLoading] = useSimpleCollection<Challenge>(`events/${eventId}/challenges`);
  const [event, evLoading] = useSimpleDocument<AppEvent>(`events/${eventId}`);
  const { homepage, logoUrl, brand } = useBranding();

  // If no valid eventId passed, redirect to homepage
  useLayoutEffect(() => {
    if (!eventId) {
      console.error("No valid eventId passed to public betting results");
      window.location.href = homepage;
    }
  }, [eventId, homepage]);

  // Find the team based on the parameter (could be ID or name)
  const targetTeam = useMemo(() => {
    if (!teams || !teamParam) return null;

    // First try to find by ID
    let team = teams.find(t => t.id === teamParam);

    // If not found, try to find by name (case-insensitive)
    if (!team) {
      team = teams.find(t =>
        t.name.toLowerCase() === teamParam.toLowerCase() ||
        t.displayName?.toLowerCase() === teamParam.toLowerCase()
      );
    }

    return team;
  }, [teams, teamParam]);

  // Find betting challenges
  const bettingChallenges = useMemo(() => {
    if (!challenges) return [];
    return challenges.filter(challenge =>
      challenge.type === "betting" &&
      (challenge as BettingChallenge).bettingsClosed
    ) as BettingChallenge[];
  }, [challenges]);

  // Get the first betting challenge (assuming one per event for now)
  const firstBettingChallenge = bettingChallenges[0];

  // Find team-specific results and payment obligations
  const teamBettingResult = useMemo(() => {
    if (!firstBettingChallenge || !targetTeam) return null;

    const bettingResults = firstBettingChallenge.bettingResults || [];
    return bettingResults.find(result => result.teamId === targetTeam.id);
  }, [firstBettingChallenge, targetTeam]);

  // Get payment transactions involving this team
  const teamPaymentTransactions = useMemo(() => {
    if (!firstBettingChallenge || !targetTeam) return { owes: [], owedBy: [] };

    const paymentTransactions = firstBettingChallenge.paymentTransactions || [];

    const owes = paymentTransactions.filter(transaction =>
      transaction.fromTeamId === targetTeam.id
    );

    const owedBy = paymentTransactions.filter(transaction =>
      transaction.toTeamId === targetTeam.id
    );

    return { owes, owedBy };
  }, [firstBettingChallenge, targetTeam]);

  const imageUrl = useFirebaseCachedImageUrl(`events/${eventId}/background_light`, `events/${eventId}/background.jpg`);
  const defaultUrl = event?.type === "wedding" ? "/bg-wedding.webp" : "/bg-conference.webp";
  const backgroundImage = evLoading ? "" : imageUrl || defaultUrl;

  // Payment handling functions
  const handleSettleUp = (transaction: PaymentTransaction) => {
    setSelectedPayment(transaction);
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedPayment(null);
  };

  const getTeamPaymentUrl = (transaction: PaymentTransaction) => {
    // Find the recipient team to get their payment URL
    const recipientTeam = teams?.find(team => team.id === transaction.toTeamId);
    return recipientTeam?.paymentUrl || null;
  };

  const copyPaymentUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      // You could add a toast notification here if you have a toast system
      alert(t("urlCopied") || "URL copied to clipboard!");
    } catch (err) {
      console.error('Failed to copy URL:', err);
      alert(t("copyFailed") || "Failed to copy URL. Please copy manually.");
    }
  };

  if (tLoading || cLoading || evLoading) {
    return <Loading loading={true}><div /></Loading>;
  }

  if (!targetTeam) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="navbar top-0 z-10 flex justify-between border-b-4 px-2 shadow-lg shadow-base-300">
          <div className="ml-4">
            <a href={homepage}>
              <img className={`color-white ${brand === "tuokis" ? "h-16" : "h-5"}`} src={logoUrl} alt="Logo" />
            </a>
          </div>
        </div>
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <h1 className="text-2xl font-semibold mb-4">{t("teamNotFound")}</h1>
            <p className="text-gray-600">{t("teamNotFoundMessage")}</p>
          </div>
        </main>
      </div>
    );
  }

  if (!firstBettingChallenge) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="navbar top-0 z-10 flex justify-between border-b-4 px-2 shadow-lg shadow-base-300">
          <div className="ml-4">
            <a href={homepage}>
              <img className={`color-white ${brand === "tuokis" ? "h-16" : "h-5"}`} src={logoUrl} alt="Logo" />
            </a>
          </div>
        </div>
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <h1 className="text-2xl font-semibold mb-4">{t("noBettingChallengesFound")}</h1>
            <p className="text-gray-600">{t("bettingNotClosedMessage")}</p>
          </div>
        </main>
      </div>
    );
  }

  const totalOwes = teamPaymentTransactions.owes.reduce((sum, transaction) => sum + transaction.amount, 0);
  const totalOwedBy = teamPaymentTransactions.owedBy.reduce((sum, transaction) => sum + transaction.amount, 0);
  const netResult = teamBettingResult?.netResult || 0;

  return (
    <div className="flex flex-col min-h-screen">
      <div className="navbar top-0 z-10 flex justify-between border-b-4 px-2 shadow-lg shadow-base-300">
        <div className="ml-4">
          <a href={homepage}>
            <img className={`color-white ${brand === "tuokis" ? "h-16" : "h-5"}`} src={logoUrl} alt="Logo" />
          </a>
        </div>
        <div className="align-end muted mr-2 flex flex-col text-xs">
          <div>{t("tryApp")}</div>
          <a href={homepage} className="link self-end">
            {t("learnMore")}
          </a>
        </div>
      </div>

      <main className="flex-1 flex-col p-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="mt-6 mb-4 text-center text-3xl font-semibold">{t("bettingResults")}</h1>
          <h2 className="mb-8 text-center text-xl text-gray-600">{event?.name}</h2>

          <div className="flex justify-center mb-8">
            <img
              className="fadein h-32 w-32 rounded-full border border-4 border-base-300 object-cover"
              src={backgroundImage}
              alt=""
            />
          </div>

          {/* Team Summary Card */}
          <div className="mb-8 rounded-lg border border-base-300 bg-base-100 p-6 shadow-lg">
            <h3 className="text-2xl font-semibold text-center mb-4">{targetTeam.displayName || targetTeam.name}</h3>

            {teamBettingResult && (
              <div className="text-center mb-4">
                <div className={`text-3xl font-bold ${netResult > 0 ? 'text-success' : netResult < 0 ? 'text-error' : 'text-neutral'}`}>
                  {netResult > 0 ? '+' : ''}${netResult.toFixed(0)}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {netResult > 0 ? t("totalWinnings") : netResult < 0 ? t("totalLosses") : t("breakEven")}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {teamBettingResult.correctBets}/{teamBettingResult.totalBets} {t("correctBets")}
                </p>
              </div>
            )}
          </div>

          {/* Final Outcomes */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">{t("finalOutcomes")}</h3>
            <div className="space-y-3">
              {firstBettingChallenge.scenarios.map((scenario, index) => {
                const correctOutcome = scenario.correctOutcome;
                const optionKey = `option${correctOutcome}` as keyof BettingScenario;
                const correctOptionText = correctOutcome !== undefined
                  ? getTranslatedValue(scenario, optionKey)
                  : "Not resolved";

                return (
                  <div key={scenario.id || index} className="rounded-lg border border-success bg-success bg-opacity-10 p-4">
                    <p className="font-medium mb-2">
                      {getTranslatedValue(scenario, "scenario")}
                    </p>
                    <p className="text-sm text-success">
                      ✓ {correctOutcome !== undefined
                        ? `${t("option")} ${correctOutcome + 1}: ${correctOptionText}`
                        : "Not resolved"}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Payment Obligations */}
          {(teamPaymentTransactions.owes.length > 0 || teamPaymentTransactions.owedBy.length > 0) && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4">{t("paymentObligations")}</h3>

              {/* Money you owe */}
              {teamPaymentTransactions.owes.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-medium mb-3 text-neutral">{t("youOwe")}</h4>
                  <div className="space-y-2">
                    {teamPaymentTransactions.owes.map((transaction, index) => (
                      <div key={index} className="flex justify-between items-center py-3 px-4 rounded-lg border border-base-300 bg-error bg-opacity-10">
                        <div className="flex-1">
                          <p className="font-medium">{transaction.toTeamName}</p>
                          <p className="text-sm text-gray-600">{t("paymentTo")}</p>
                        </div>
                        <div className="text-right flex items-center space-x-3">
                          <p className="text-xl font-bold text-error">${transaction.amount.toFixed(0)}</p>
                          <button
                            onClick={() => handleSettleUp(transaction)}
                            className="btn btn-sm btn-primary"
                          >
                            {t("settleUp") || "Settle Up"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-3 bg-error bg-opacity-5 rounded-lg border border-error border-opacity-20">
                    <p className="text-sm font-medium text-error">
                      {t("totalYouOwe")}: ${totalOwes.toFixed(0)}
                    </p>
                  </div>
                </div>
              )}

              {/* Money owed to you */}
              {teamPaymentTransactions.owedBy.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-medium mb-3 text-neutral">{t("owedToYou")}</h4>
                  <div className="space-y-2">
                    {teamPaymentTransactions.owedBy.map((transaction, index) => (
                      <div key={index} className="flex justify-between items-center py-3 px-4 rounded-lg border border-base-300 bg-success bg-opacity-10">
                        <div>
                          <p className="font-medium">{transaction.fromTeamName}</p>
                          <p className="text-sm text-gray-600">{t("paymentFrom")}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-success">+${transaction.amount.toFixed(0)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-3 bg-success bg-opacity-5 rounded-lg border border-base-300 border-opacity-20">
                    <p className="text-sm font-medium text-neutral">
                      {t("totalOwedToYou")}: ${totalOwedBy.toFixed(0)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No payment obligations */}
          {teamPaymentTransactions.owes.length === 0 && teamPaymentTransactions.owedBy.length === 0 && (
            <div className="mb-8 text-center">
              <div className="rounded-lg border border-base-300 bg-base-200 p-6">
                <p className="text-lg font-medium text-neutral">{t("noPaymentObligations")}</p>
                <p className="text-sm text-gray-600 mt-2">{t("noPaymentObligationsMessage")}</p>
              </div>
            </div>
          )}

          {/* Footer message */}
          <div className="mt-12 text-center text-sm text-gray-500">
            <p>{t("bettingResultsFooter")}</p>
          </div>
        </div>
      </main>

      {/* Payment Modal */}
      <Modal show={showPaymentModal} onCancel={closePaymentModal}>
        {selectedPayment && (
          <div className="p-6">
            <h3 className="text-xl font-semibold mb-4 text-center">
              {t("paymentInstructions") || "Payment Instructions"}
            </h3>

            <div className="mb-6 text-center">
              <p className="text-lg mb-2">
                {t("paymentTo") || "Pay"}: <span className="font-semibold">{selectedPayment.toTeamName}</span>
              </p>
              <p className="text-3xl font-bold text-primary">
                ${selectedPayment.amount.toFixed(0)}
              </p>
            </div>

            {(() => {
              const paymentUrl = getTeamPaymentUrl(selectedPayment);

              if (!paymentUrl) {
                return (
                  <div className="mb-6">
                    <div className="bg-warning bg-opacity-10 border border-warning rounded-lg p-4 text-center">
                      <p className="text-sm font-medium text-warning mb-2">
                        {t("noPaymentUrlSet") || "No Payment URL Set"}
                      </p>
                      <p className="text-xs text-gray-600">
                        {t("contactTeamDirectly") || "Please contact this team directly to arrange payment."}
                      </p>
                    </div>
                  </div>
                );
              }

              return (
                <>
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-3">
                      {t("paymentUrlInstructions") || "Use the following URL to pay:"}
                    </p>

                    <div className="bg-base-200 p-3 rounded-lg border">
                      <p className="text-sm font-mono break-all text-center">
                        {paymentUrl}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col space-y-3">
                    <button
                      onClick={() => copyPaymentUrl(paymentUrl)}
                      className="btn btn-primary w-full"
                    >
                      {t("copyUrl") || "Copy Payment URL"}
                    </button>

                    <a
                      href={paymentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline w-full"
                    >
                      {t("openPaymentApp") || "Open Payment App"}
                    </a>
                  </div>
                </>
              );
            })()}

            <button
              onClick={closePaymentModal}
              className="btn btn-ghost w-full mt-3"
            >
              {t("close") || "Close"}
            </button>

            <div className="mt-4 text-xs text-gray-500 text-center">
              <p>{t("paymentDisclaimer") || "Please coordinate with the recipient to confirm payment method preferences."}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
